import React, { useState, useMemo } from "react";
import Tooltip from "@mui/material/Tooltip";
import {
  parseBits,
  formatTime,
  getTotalTimeForBody,
  getBitTimings,
  DEFAULT_WORDS_PER_MINUTE,
} from "./documentBodyUtils";
import PracticeBody from "./PracticeBody";
import WysiwygBody from "./WysiwygBody";

// A single-letter mnemonic badge for the formatting toolbar (P for
// Punchline, B for Bit header, S for Stage direction), used as the
// "icon" sitting above each button's short label. The matching Edit-menu
// items use the same badge (see LetterIcon in EditorMenu.js).
function LetterIcon({ children, selected }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
        height: 18,
        borderRadius: 4,
        backgroundColor: selected ? "rgba(11,93,40,0.16)" : "rgba(0,0,0,0.06)",
        color: selected ? "#0b5d28" : undefined,
        fontWeight: 700,
        fontSize: 11,
        lineHeight: 1,
      }}
    >
      {children}
    </span>
  );
}

// A toolbar button in the icon-over-label style: a small letter badge on
// top, a short phrase underneath - taller and more deliberate than a bare
// icon button, closer to a typical word-processor/screenwriting toolbar.
function ToolbarButton({ letter, label, title, selected, onMouseDown }) {
  return (
    <Tooltip title={title}>
      <button
        type="button"
        onMouseDown={onMouseDown}
        className={`flex flex-col items-center justify-center gap-0.5 px-3 py-1.5 rounded transition-colors ${
          selected ? "bg-emerald-100" : "hover:bg-gray-200"
        }`}
      >
        <LetterIcon selected={selected}>{letter}</LetterIcon>
        <span
          className={`text-[10px] leading-none ${
            selected ? "text-emerald-900 font-semibold" : "text-gray-600"
          }`}
        >
          {label}
        </span>
      </button>
    </Tooltip>
  );
}

// Format jokes (separated by blank lines) into HTML elements
export function formatJokesToElements(lines, bitIndex) {
  let elements = [];
  let currentJoke = [];
  lines.forEach((line, i) => {
    if (line.trim() === "") {
      if (currentJoke.length > 0) {
        elements.push(
          <div key={`bit-${bitIndex}-joke-${i}`}>{currentJoke}</div>
        );
        currentJoke = [];
      }
      return;
    }

    const parsedParts = parseJokeLine(line);

    parsedParts.forEach((part, j) => {
      if (part.punchline !== undefined) {
        currentJoke.push(
          <b key={`bit-${bitIndex}-punchline-${i}-${j}`}>{part.punchline}</b>
        );
      } else if (part.stage !== undefined) {
        currentJoke.push(
          <i
            key={`bit-${bitIndex}-stage-${i}-${j}`}
            className="text-gray-500 italic" // darkened from gray-400
          >
            {part.stage}
          </i>
        );
      } else if (part.text !== undefined) {
        currentJoke.push(
          <span key={`bit-${bitIndex}-plain-${i}-${j}`}>{part.text}</span>
        );
      }
    });
    // Add <br /> after each line except the last non-empty line in the joke
    currentJoke.push(<br key={`bit-${bitIndex}-br-${i}`} />);
  });
  // Remove trailing <br /> if present
  if (currentJoke.length > 0 && currentJoke[currentJoke.length - 1]?.type === "br") {
    currentJoke.pop();
  }
  // Push last joke if any
  if (currentJoke.length > 0) {
    elements.push(<div key={`bit-${bitIndex}-joke-last`}>{currentJoke}</div>);
  }
  return elements;
}

export function parseJokeLine(line) {
  const starRegex = /\*\s*([^\s\*\.()]+)(\.)?/g;
  const parenRegex = /\(([^)]+)\)/g;
  const bracketRegex = /\[([^\]]+)\]/g;

  let result = [];
  let lastIndex = 0;

  const matches = [];
  let match;
  while ((match = starRegex.exec(line)) !== null) {
    matches.push({ type: "star", index: match.index, length: match[0].length, punchline: match[1], dot: !!match[2] });
  }
  while ((match = parenRegex.exec(line)) !== null) {
    matches.push({ type: "paren", index: match.index, length: match[0].length, punchline: match[1] });
  }
  while ((match = bracketRegex.exec(line)) !== null) {
    // keep brackets visible
    matches.push({ type: "stage", index: match.index, length: match[0].length, stage: match[0] });
  }

  matches.sort((a, b) => a.index - b.index);

  matches.forEach((m) => {
    if (m.index > lastIndex) {
      result.push({ text: line.slice(lastIndex, m.index) });
    }
    if (m.type === "stage") {
      result.push({ stage: m.stage }); // includes brackets
    } else {
      result.push({ punchline: m.punchline });
      if (m.type === "star" && m.dot) {
        result.push({ text: "." });
      }
    }
    lastIndex = m.index + m.length;
  });
  if (lastIndex < line.length) {
    result.push({ text: line.slice(lastIndex) });
  }
  return result.length ? result : [{ text: line }];
}

// Helper to count jokes in a bit (non-empty blocks separated by blank lines)
function countJokes(lines) {
  // Count total '(' and '*' in all lines
  let count = 0;
  lines.forEach(line => {
    count += (line.match(/\(/g) || []).length;
    count += (line.match(/\*/g) || []).length;
  });
  return count;
}

function getJokeDensityText(secondsPerJoke) {
  if (!secondsPerJoke || secondsPerJoke === 0) return "0";
  return `${secondsPerJoke}`;
}

// Remove stage directions ([...]) for timing/word counts
function stripStageDirections(str) {
  return str.replace(/\[[^\]]+\]/g, "");
}

// Render the header for a bit
function renderBitHeader(bit, bitIndex, open, toggleOpen, startTime, endTime, secondsPerJoke) {
  const densityText = getJokeDensityText(secondsPerJoke);
  return (
    <div
      key={`bit-header-${bitIndex}`}
      className={`mt-4 mb-2 text-sm font-semibold cursor-pointer transition
        ${open ? "bg-gray-100 text-emerald-900" : "bg-gray-50 text-gray-700"}
        px-4 py-2 text-center border border-gray-300 shadow-sm flex justify-between items-center`}
      style={{
        width: "100%",
        userSelect: "none",
        borderRadius: 0,
        boxShadow: "0 1px 4px 0 rgba(0,0,0,0.04)",
        textAlign: "center", // Ensure centering
      }}
      onClick={() => toggleOpen(bitIndex)}
      tabIndex={0}
      role="button"
      aria-pressed={open}
    >
      <span style={{ flex: "1 1 auto", textAlign: "center" }}>
        {bit.name
          ? `${bit.name} - ${startTime}–${endTime}`
          : `${startTime}–${endTime}`}
      </span>
      <span
        style={{ textAlign: "right", flex: "0 0 auto", minWidth: "60px" }}
        title="seconds per joke"
      >
        {densityText}
      </span>
    </div>
  );
}

// Render a single bit and return { header, content, wordCount }
function renderBit(bit, bitIndex, open, toggleOpen, startSeconds = 0, endSeconds = 0) {
  if (bit.name === null && (!bit.content || bit.content.length === 0)) {
    return { header: null, content: null, wordCount: 0 };
  }

  const text = bit.content.join("\n");
  // Exclude stage directions from word count used in metadata
  const wordCount = stripStageDirections(text).split(/\s+/).filter(Boolean).length;

  // Format start and end times
  const startTime = formatTime(startSeconds);
  const endTime = formatTime(endSeconds);

  // Count jokes and calculate seconds per joke
  const jokes = countJokes(bit.content);
  let duration = endSeconds - startSeconds;
  // Ensure duration is at least 1 second per joke if jokes exist
  if (jokes > 0 && duration < jokes) {
    duration = jokes;
  }
  // FIX: Calculate seconds per joke correctly
  const secondsPerJoke = jokes > 0 ? Math.round(duration / jokes) : null;

  const header = renderBitHeader(
    bit,
    bitIndex,
    open,
    toggleOpen,
    startTime,
    endTime,
    secondsPerJoke
  );

  let content = null;
  if (open) {
    const elements = formatJokesToElements(bit.content, bitIndex);
    content = (
      <div
        key={`bit-content-${bitIndex}`}
        className="mb-4"
        style={{ marginTop: "8px" }}
      >
        {elements}
      </div>
    );
  }

  return { header, content, wordCount };
}

function getFormattedBitsAndTotalTime(body, openBits, toggleOpen, wordsPerMinute) {
  const bits = parseBits(body);
  const formatted = [];

  // Calculate start/end times for each bit excluding stage directions
  const bitTimings = getBitTimings(bits, wordsPerMinute);

  // Render bits with timing info
  if (bits.length > 0) {
    bits.forEach((bit, b) => {
      const { header, content } = renderBit(
        bit,
        b,
        openBits[b],
        toggleOpen,
        bitTimings[b]?.start ?? 0,
        bitTimings[b]?.end ?? 0
      );
      if (header) formatted.push(<div key={`bit-wrap-${b}`} style={{ textAlign: "center" }}>{header}</div>);
      if (content) formatted.push(content);
    });
  }

  const totalTime = getTotalTimeForBody(body, wordsPerMinute);

  formatted.push(
    <div
      key="total-time"
      className="mt-8 mb-2 text-sm text-gray-400 font-semibold"
      style={{ textAlign: "right" }}
    >
      Total - {totalTime}
    </div>,
  );

  return formatted;
}

function renderEditTextarea(body, setBody, disabled, onEditAttempt) {
  return (
    <textarea
      value={body}
      onChange={e => {
        if (!disabled && (!onEditAttempt || onEditAttempt())) {
          setBody(e.target.value);
        }
      }}
      readOnly={disabled}
      className="w-full flex-1 min-h-0 border rounded px-2 py-1 mt-1 resize-none"
      style={{ fontFamily: "'Courier Prime', 'Courier New', monospace" }}
      required
    />
  );
}

export default function DocumentBody({
  mode,
  body,
  setBody,
  disabled,
  onEditAttempt,
  wordsPerMinute = DEFAULT_WORDS_PER_MINUTE,
  rawView = false,
  wysiwygRef,
  onActiveLineChange,
  lineFocused = false,
  isHeaderActive = false,
  onApplyBold,
  onApplyBit,
  onApplyStage,
}) {
  // Memoized so PracticeBody doesn't re-measure its layout on every re-render
  const bits = useMemo(() => parseBits(body), [body]);

  // Detect if on phone (screen width <= 600px)
  const isPhone = typeof window !== "undefined" && window.matchMedia("(max-width: 600px)").matches;

  // Set initial openBits based on device type
  const [openBits, setOpenBits] = useState(() =>
    Array(bits.length).fill(!isPhone)
  );

  // Update openBits when body changes
  React.useEffect(() => {
    setOpenBits(Array(bits.length).fill(!isPhone));
    // eslint-disable-next-line
  }, [body]);

  const toggleOpen = (idx) => {
    setOpenBits((prev) => {
      const copy = [...prev];
      copy[idx] = !copy[idx];
      return copy;
    });
  };

  if (mode === "edit") {
    if (rawView) {
      return renderEditTextarea(body, setBody, disabled, onEditAttempt);
    }
    // The buttons are never shown disabled - clicking one when nothing's
    // focused is already a harmless no-op (applyBold/applyBit/applyStage
    // all guard on that internally), so there's no need for the usual
    // grayed-out MUI disabled look here. Left padding lines the first
    // button up with "File" in the menu above.
    return (
      <div className="w-full flex-1 min-h-0 flex flex-col">
        <div className="flex items-center justify-center gap-1 bg-gray-100 flex-shrink-0">
          <ToolbarButton
            letter="P"
            label="Punchline"
            title="Punchline (P): mark the selected text as a punchline, shown bold"
            onMouseDown={(e) => {
              e.preventDefault();
              onApplyBold && onApplyBold();
            }}
          />
          <ToolbarButton
            letter="B"
            label="Bit"
            selected={isHeaderActive}
            title={
              isHeaderActive
                ? "Bit Header (B): turn this line back into plain text"
                : "Bit Header (B): turn this line into a bit header"
            }
            onMouseDown={(e) => {
              e.preventDefault();
              onApplyBit && onApplyBit();
            }}
          />
          <ToolbarButton
            letter="S"
            label="Stage"
            title="Stage Direction (S): mark the selected text as a stage direction, shown gray and italic"
            onMouseDown={(e) => {
              e.preventDefault();
              onApplyStage && onApplyStage();
            }}
          />
        </div>
        <WysiwygBody
          ref={wysiwygRef}
          body={body}
          setBody={setBody}
          disabled={disabled}
          onEditAttempt={onEditAttempt}
          onActiveLineChange={onActiveLineChange}
          wordsPerMinute={wordsPerMinute}
        />
      </div>
    );
  }

  // Practice mode: the whole script as one continuously scrolling teleprompter
  if (mode === "practice") {
    return (
        <PracticeBody bits={bits} wordsPerMinute={wordsPerMinute} />
    );
  }

  const formatted = getFormattedBitsAndTotalTime(body, openBits, toggleOpen, wordsPerMinute);

  return (
    <div
      className="w-full flex-1 min-h-0 border rounded px-2 py-1 mt-1 bg-gray-50"
      style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowY: "auto",
        fontFamily: "'Courier Prime', 'Courier New', monospace",
        fontSize: "inherit",
        minHeight: "120px",
      }}
    >
      {formatted}
    </div>
  );
}
